/**
 * GS1 utilities — GTIN-14 / SSCC-18 normalization, URNs, and barcode AI strings.
 */

/**
 * Calculate the GTIN/SSCC check digit using the GS1 mod-10 algorithm.
 *
 * @param {string} digits - GTIN/SSCC body without check digit (padded to 13 for GTIN-14)
 * @returns {string} - Single digit check digit
 */
export function calcGtinCheckDigit(digits) {
  if (!digits) return '0';
  const padded = String(digits).padStart(13, '0');
  let sum = 0;
  for (let i = 0; i < padded.length; i++) {
    const d = parseInt(padded[i], 10);
    sum += d * (i % 2 === 0 ? 3 : 1);
  }
  const remainder = sum % 10;
  return String(remainder === 0 ? 0 : 10 - remainder);
}

/**
 * Normalize any GTIN-12/13/14 input to canonical GTIN-14 with a valid check digit.
 *
 * EAN-13 and UPC-A are converted by padding the data field to 13 digits (GTIN-14
 * body) and recalculating the check digit — never padStart(14) on the full string
 * including the old check digit.
 *
 * @param {string} gtin - Raw GTIN (12-14 digits)
 * @returns {string} - 14-digit GTIN-14 or empty string if invalid length
 */
export function normalizeGtin14(gtin) {
  if (!gtin) return '';
  const digits = String(gtin).replace(/\D/g, '');
  if (digits.length < 12 || digits.length > 14) return '';

  const gtin14Body = digits.slice(0, -1).padStart(13, '0');
  const check = calcGtinCheckDigit(gtin14Body);
  return gtin14Body + check;
}

/**
 * Normalize SSCC input to canonical 18-digit form with valid check digit.
 *
 * @param {string} sscc - Raw SSCC (17-18 digits)
 * @returns {string} - 18-digit SSCC or empty string
 */
export function normalizeSscc18(sscc) {
  if (!sscc) return '';
  const digits = String(sscc).replace(/\D/g, '');
  if (digits.length < 17 || digits.length > 18) return '';

  const sscc17Body = digits.slice(0, -1).padStart(17, '0').slice(-17);
  const check = calcGtinCheckDigit(sscc17Body);
  return sscc17Body + check;
}

/**
 * True when normalizeGtin14 would change the digit sequence (EAN-13 → GTIN-14, bad check, etc.)
 */
export function gtinNeedsNormalization(gtin) {
  const digits = String(gtin || '').replace(/\D/g, '');
  if (digits.length < 12) return false;
  const normalized = normalizeGtin14(gtin);
  return !!normalized && normalized !== digits;
}

/**
 * Validate GTIN as canonical GTIN-14 (after normalization).
 */
export function validateGtinChecksum(gtin) {
  const normalized = normalizeGtin14(gtin);
  if (!normalized) return false;
  const digits = String(gtin).replace(/\D/g, '');
  if (digits.length < 12 || digits.length > 14) return false;
  return normalized === normalizeGtin14(digits);
}

/**
 * @deprecated Prefer normalizeGtin14 — kept for existing call sites.
 */
export function correctGtinChecksum(gtin) {
  return normalizeGtin14(gtin);
}

/**
 * Validate SSCC as canonical SSCC-18 (after normalization).
 */
export function validateSsccChecksum(sscc) {
  const normalized = normalizeSscc18(sscc);
  if (!normalized) return false;
  const digits = String(sscc).replace(/\D/g, '');
  if (digits.length < 17 || digits.length > 18) return false;
  return normalized === normalizeSscc18(digits);
}

/**
 * @deprecated Prefer normalizeSscc18 — kept for existing call sites.
 */
export function correctSsccChecksum(sscc) {
  return normalizeSscc18(sscc);
}

/**
 * Normalize GTIN input for form handlers — returns { value, corrected }.
 */
export function applyGtinInputNormalization(rawValue) {
  const digits = String(rawValue || '').replace(/\D/g, '');
  if (digits.length < 12) {
    return { value: rawValue, corrected: false };
  }
  const normalized = normalizeGtin14(digits);
  if (!normalized) {
    return { value: rawValue, corrected: false };
  }
  return {
    value: normalized,
    corrected: normalized !== digits,
  };
}

/**
 * Normalize SSCC input for form handlers — returns { value, corrected }.
 */
export function applySsccInputNormalization(rawValue) {
  const digits = String(rawValue || '').replace(/\D/g, '');
  if (digits.length < 17) {
    return { value: rawValue, corrected: false };
  }
  const normalized = normalizeSscc18(digits);
  if (!normalized) {
    return { value: rawValue, corrected: false };
  }
  return {
    value: normalized,
    corrected: normalized !== digits,
  };
}

/**
 * Format a GTIN-14 (or GTIN-13/GTIN-12) into a canonical GS1 LGTIN URN.
 */
export function formatLgtinUrn(gtin, lot = '') {
  const normalized = normalizeGtin14(gtin);
  if (!normalized) return null;

  const withoutCheck = normalized.slice(0, 13);
  const companyPrefix = withoutCheck.slice(0, 7);
  const itemRef = withoutCheck.slice(7);

  return `urn:epc:class:lgtin:${companyPrefix}.${itemRef}.${lot || ''}`;
}

/**
 * Format a GTIN into a canonical GS1 SGTIN URN.
 */
export function formatSgtinUrn(gtin, serial = '') {
  const normalized = normalizeGtin14(gtin);
  if (!normalized) return null;

  const withoutCheck = normalized.slice(0, 13);
  const companyPrefix = withoutCheck.slice(0, 7);
  const itemRef = withoutCheck.slice(7);

  return `urn:epc:id:sgtin:${companyPrefix}.${itemRef}.${serial}`;
}

/**
 * Format an SSCC into a canonical GS1 SSCC URN.
 * URN serial (9 digits) = extension digit + first 8 digits of SSCC serial reference.
 */
export function formatSsccUrn(sscc) {
  const normalized = normalizeSscc18(sscc);
  if (!normalized) return null;

  const withoutCheck = normalized.slice(0, 17);
  const extensionDigit = withoutCheck.charAt(0);
  const companyPrefix = withoutCheck.slice(1, 8);
  const serialField = withoutCheck.slice(8, 17);
  const urnSerial = (extensionDigit + serialField.slice(0, 8)).padStart(9, '0');

  return `urn:epc:id:sscc:${companyPrefix}.${urnSerial}`;
}

export function toGs1Uom(unit) {
  const map = {
    units: 'EA',
    cases: 'CS',
    pallets: 'PCE',
    kg: 'KGM',
    boxes: 'BX',
    crates: 'CRT',
  };
  return map[unit] || 'EA';
}

export function parseLgtinUrn(urn) {
  if (!urn) return null;
  const match = urn.match(/^urn:epc:class:lgtin:(\d+)\.(\d+)\.(.+)$/);
  if (!match) return null;
  const [, companyPrefix, itemRef, lot] = match;

  const gtin13 = '0' + companyPrefix + itemRef;
  const checkDigit = calcGtinCheckDigit(gtin13);
  const gtin14 = gtin13 + checkDigit;

  return { companyPrefix, itemRef, lot, gtin14: normalizeGtin14(gtin14) };
}

export function parseSsccUrn(urn) {
  if (!urn) return null;
  const match = urn.match(/^urn:epc:id:sscc:(\d+)\.(\d+)$/);
  if (!match) return null;
  const [, companyPrefix, serialRef] = match;

  const urnSerial = serialRef.padStart(9, '0');
  const extensionDigit = urnSerial.charAt(0);
  const serialRef8 = urnSerial.slice(1).padStart(8, '0');
  const serialField = (serialRef8 + '0').slice(0, 9);
  const sscc17 = extensionDigit + companyPrefix.padStart(7, '0') + serialField;

  return normalizeSscc18(sscc17 + calcGtinCheckDigit(sscc17));
}

/**
 * GS1-128 AI (00) — SSCC only (logistic unit symbol, never combined with other AIs).
 */
export function buildGs1SsccAiString({ sscc }) {
  const normalized = normalizeSscc18(sscc);
  if (!normalized) return '';
  return `(00)${normalized}`;
}

/**
 * GS1-128 logistic content — AI (01) alone, or AI (02)+(37) when quantity present.
 */
export function buildGs1LogisticContentAiString({ gtin, lot, expiryDate, quantity }) {
  const gtin14 = normalizeGtin14(gtin);
  if (!gtin14) return '';

  const qtyPresent = quantity != null && quantity !== '';
  const aiCode = qtyPresent ? '02' : '01';
  let s = `(${aiCode})${gtin14}`;
  if (lot) s += `(10)${lot}`;
  if (expiryDate) {
    const d = new Date(expiryDate);
    const yy = String(d.getFullYear()).slice(2);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    s += `(17)${yy}${mm}${dd}`;
  }
  if (qtyPresent) s += `(37)${quantity}`;
  return s;
}

/**
 * GS1-128 trade item — AI (01) + (10) + (17), no count.
 */
export function buildGs1TradeItemAiString({ gtin, lot, expiryDate }) {
  const gtin14 = normalizeGtin14(gtin);
  if (!gtin14) return '';

  let s = `(01)${gtin14}`;
  if (lot) s += `(10)${lot}`;
  if (expiryDate) {
    const d = new Date(expiryDate);
    const yy = String(d.getFullYear()).slice(2);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    s += `(17)${yy}${mm}${dd}`;
  }
  return s;
}

/**
 * Combined AI string for GS1 DataMatrix pallet encoding only.
 * Do NOT use for a single GS1-128 symbol — use buildGs1SsccAiString and
 * buildGs1LogisticContentAiString in separate symbols per GS1 Logistic Label rules.
 */
export function buildGs1AiString(params) {
  return buildGs1SsccAiString(params) + buildGs1LogisticContentAiString(params);
}

export function buildGs1CountAiString({ quantity }) {
  if (quantity == null || quantity === '') return '';
  return `(37)${quantity}`;
}

export function buildGs1DataMatrixString({ gtin, lot, sscc, quantity, expiryDate }) {
  const GS = String.fromCharCode(29);
  let s = '';

  const ssccNorm = normalizeSscc18(sscc);
  if (ssccNorm) s += `(00)${ssccNorm}`;

  const gtin14 = normalizeGtin14(gtin);
  if (gtin14) s += `(01)${gtin14}`;

  if (lot) s += `(10)${lot}${GS}`;

  if (expiryDate) {
    const d = new Date(expiryDate);
    const yy = String(d.getFullYear()).slice(2);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    s += `(17)${yy}${mm}${dd}`;
  }

  if (!gtin14 && quantity != null && quantity !== '') {
    s += `(37)${quantity}`;
  }

  return s;
}

export function buildGs1DigitalLinkUrl({ gtin, lot, sscc, quantity, expiryDate }) {
  const base = 'https://id.chillverify.com';
  const pathParts = [];
  const queryParams = [];

  const gtin14 = normalizeGtin14(gtin);
  if (gtin14) pathParts.push('01', gtin14);

  if (lot) pathParts.push('10', encodeURIComponent(lot));

  const ssccNorm = normalizeSscc18(sscc);
  if (ssccNorm) pathParts.push('00', ssccNorm);

  if (quantity != null && quantity !== '') queryParams.push(`37=${quantity}`);
  if (expiryDate) {
    const d = new Date(expiryDate);
    const yy = String(d.getFullYear()).slice(2);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    queryParams.push(`17=${yy}${mm}${dd}`);
  }

  let url = base;
  if (pathParts.length) url += `/${pathParts.join('/')}`;
  if (queryParams.length) url += `?${queryParams.join('&')}`;
  return url;
}

export function extractGs1Data(shipment) {
  const meta = shipment?.metadata || {};
  let gtin = meta.gtin ? normalizeGtin14(meta.gtin) : '';
  let lot = meta.lot_number || '';
  let sscc = meta.sscc ? normalizeSscc18(meta.sscc) : '';
  let quantity = meta.quantity;
  let expiryDate = meta.expiry_date || '';

  if (!gtin && shipment?.epc_uri) {
    const parsed = parseLgtinUrn(shipment.epc_uri);
    if (parsed) {
      gtin = parsed.gtin14 || '';
      if (!lot) lot = parsed.lot;
    }
  }

  if (!sscc && shipment?.epc_uri) {
    const ssccParsed = parseSsccUrn(shipment.epc_uri);
    if (ssccParsed) sscc = ssccParsed;
  }

  return { gtin, lot, sscc, quantity, expiryDate };
}

/** GS1 example company prefix used for demo / test identifier generation. */
export const GS1_DEMO_COMPANY_PREFIX = '614141';

/** GS1 example GTIN-14 (valid check digit) for demo autogeneration. */
export const GS1_DEMO_GTIN = '00614141000005';

/**
 * Generate a batch/lot identifier — e.g. LOT-20260830-A1B2 or numeric LOT-1234567.
 * @param {{ numeric?: boolean }} options - numeric: true → LOT-1234567 style
 */
export function generateLotNumber(options = {}) {
  if (options.numeric) {
    const n = Math.floor(1000000 + Math.random() * 9000000);
    return String(n);
  }
  const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const suffix = (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().slice(0, 4)
    : Math.floor(Math.random() * 65536).toString(16).padStart(4, '0')
  ).toUpperCase();
  return `LOT-${ymd}-${suffix}`;
}

/**
 * Generate an expiry date ISO string (YYYY-MM-DD) N days from today.
 */
export function generateExpiryDateIso(daysFromNow = 90) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().slice(0, 10);
}

/**
 * Generate a unique SSCC-18 with valid check digit.
 * Structure: extension (1) + company prefix (7) + serial ref (9) + check (1).
 */
export function generateSscc18({
  extensionDigit = '0',
  companyPrefix = GS1_DEMO_COMPANY_PREFIX,
} = {}) {
  const ext = String(extensionDigit).replace(/\D/g, '').slice(0, 1) || '0';
  const prefix = String(companyPrefix).replace(/\D/g, '').padStart(7, '0').slice(-7);
  const serialRef = String(Math.floor(Math.random() * 1e9)).padStart(9, '0');
  const body17 = ext + prefix + serialRef;
  return body17 + calcGtinCheckDigit(body17);
}

/**
 * Autogenerate GS1 barcode fields for a new logistic unit shipment.
 * GTIN uses demo value unless preserveGtin supplies a valid existing GTIN.
 */
export function generateGs1ShipmentFields({
  gtin: preserveGtin,
  expiryDays = 90,
  quantity = 1,
  lotNumeric = false,
} = {}) {
  const sscc = generateSscc18();
  const gtin = normalizeGtin14(preserveGtin) || GS1_DEMO_GTIN;
  const epcUri = formatSsccUrn(sscc) || '';
  return {
    sscc,
    epcUri,
    gtin,
    lotNumber: generateLotNumber({ numeric: lotNumeric }),
    expiryDate: generateExpiryDateIso(expiryDays),
    quantity,
  };
}
